/**
 * PersonalizedQuestionChips.tsx - "Pick up where you left off" chips
 *
 * Sits above the admin-configured SuggestedQuestionChips on the empty state. The two are
 * deliberately independent: this one fetches on its own and renders null unless it has
 * something, so a failure here can never take the admin chips down with it.
 */

import { Button, Flex, HStack, Text } from '@chakra-ui/react';
import { LuSparkles } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useChatContext } from '@/contexts/ChatContext';
import { usePersonalizedQuestions, PersonalizedQuestion } from '@/hooks/usePersonalizedQuestions';
import { isRTLText } from '@/utilities/isRTLText';

export const PersonalizedQuestionChips: React.FC = () => {
    const { parentCircleId, setPendingQuestion, loading } = useChatContext();
    const { questions, isLoading } = usePersonalizedQuestions(parentCircleId || null);
    const { t, i18n } = useTranslation();

    // Nothing to say is the normal case — a new user, an unconfigured circle, a search that
    // came back empty. Render nothing rather than an empty heading.
    if (isLoading || questions.length === 0) {
        return null;
    }

    // The chips are written in the user's own language, which may not be the UI's.
    const isRTL = i18n.dir() === 'rtl' || isRTLText(questions[0]?.text);

    const handleClick = (question: PersonalizedQuestion) => {
        if (loading) return;
        setPendingQuestion(question.text);
    };

    return (
        <Flex
            className="fadeLong"
            width="100%"
            direction="column"
            alignItems="center"
            gap={{ base: 1.5, md: 2 }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            <HStack gap={1.5} color="var(--cair-text-secondary)">
                <LuSparkles size={14} />
                <Text fontSize="xs" fontWeight="medium">
                    {t('personalized_questions_title')}
                </Text>
            </HStack>

            <Flex
                width="100%"
                wrap="wrap"
                justifyContent="center"
                alignItems="center"
                gap={{ base: 1.5, md: 2 }}
            >
                {questions.map((question, index) => (
                    <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleClick(question)}
                        disabled={loading}
                        borderRadius="var(--cair-radius-lg)"
                        borderColor="var(--cair-border)"
                        background="var(--cair-surface-elevated)"
                        color="var(--cair-text)"
                        fontWeight="normal"
                        fontSize={{ base: 'sm', md: 'md' }}
                        height="auto"
                        maxWidth="100%"
                        paddingX={{ base: 3, md: 4 }}
                        paddingY={{ base: 2, md: 2.5 }}
                        whiteSpace="normal"
                        wordBreak="break-word"
                        textAlign={isRTL ? 'right' : 'left'}
                        lineHeight="1.3"
                        _hover={{
                            background: 'var(--cair-accent-subtle)',
                            borderColor: 'var(--cair-accent)',
                        }}
                    >
                        {question.text}
                    </Button>
                ))}
            </Flex>
        </Flex>
    );
};
